import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import { historyKeys } from './historyApi';
import type {
  MealPatternItem,
  MealPatternChildItem,
  MealPatternGlobalItem,
  MealPatternGlobalChildItem,
  MealPatternGlobalGroup,
  GlobalTemplateCombined,
  GlobalMealPatternUsagePatient,
  PriorityReorder,
  MealPatternAdjustmentMenuDays,
} from '../types/mealPattern';
import type { MaterialColor } from '../types/referenceData';

// --- Query Keys ---

export const mealPatternKeys = {
  all: ['mealPattern'] as const,
  byPatient: (facilityId: number, patientId: number) =>
    [...mealPatternKeys.all, 'byPatient', facilityId, patientId] as const,
  children: (facilityId: number, patientId: number) =>
    [...mealPatternKeys.all, 'children', facilityId, patientId] as const,
  globalItems: (facilityId: number) =>
    [...mealPatternKeys.all, 'globalItems', facilityId] as const,
  /** The residents holding one template (GlobalItems/Usage): the Usage dialog and the impact line share it. */
  globalItemUsage: (facilityId: number, pKey: number) =>
    [...mealPatternKeys.globalItems(facilityId), 'usage', pKey] as const,
  globalGroups: (facilityId: number) =>
    [...mealPatternKeys.all, 'globalGroups', facilityId] as const,
  globalItemsByGroup: (facilityId: number, groupPKey: number) =>
    [...mealPatternKeys.all, 'globalItemsByGroup', facilityId, groupPKey] as const,
  menuDaysByMealPattern: (facilityId: number, mealPatternId: number) =>
    [...mealPatternKeys.all, 'menuDays', facilityId, mealPatternId] as const,
};

// --- Menu Days (tblPrd_Meal_Pattern_Adjustment_MenuDays) ---

export function useMenuDaysByMealPattern(facilityId: number, mealPatternId: number) {
  return useQuery({
    queryKey: mealPatternKeys.menuDaysByMealPattern(facilityId, mealPatternId),
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternAdjustmentMenuDays[]>(
        API.mealPatternMenuDaysByMealPattern(facilityId, mealPatternId),
      );
      return data ?? [];
    },
    // Negative = the resident's global instance (-PKey_Prd_Meal_Pattern_Global);
    // the API reads its menu days by that key. Only 0 (not saved yet) has none.
    enabled: facilityId > 0 && mealPatternId !== 0,
    staleTime: 60 * 1000,
  });
}

export function useSaveMenuDays(facilityId: number, mealPatternId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (entity: MealPatternAdjustmentMenuDays) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternMenuDaysAddMod(facilityId),
        entity,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mealPatternKeys.menuDaysByMealPattern(facilityId, mealPatternId),
      });
      // The grid's calendar column reads each row's menu days from the
      // meal pattern list, so that has to refetch too.
      queryClient.invalidateQueries({ queryKey: [...mealPatternKeys.all, 'byPatient', facilityId] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to save menu days';
      addNotification(msg, 'error');
    },
  });
}

export function useDeleteMenuDays(facilityId: number, mealPatternId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.mealPatternMenuDaysDelete(facilityId, pKey));
      return pKey;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mealPatternKeys.menuDaysByMealPattern(facilityId, mealPatternId),
      });
      // The grid's calendar column reads each row's menu days from the
      // meal pattern list, so that has to refetch too.
      queryClient.invalidateQueries({ queryKey: [...mealPatternKeys.all, 'byPatient', facilityId] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to delete menu days';
      addNotification(msg, 'error');
    },
  });
}

// --- Queries ---

export function useMealPatternItems(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: mealPatternKeys.byPatient(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternItem[]>(
        API.mealPatternByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load meal pattern items', 'error'),
    },
  });
}

export function useMealPatternChildren(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: mealPatternKeys.children(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternChildItem[]>(
        API.mealPatternChildrenByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load meal pattern children', 'error'),
    },
  });
}

// --- Mutations ---

export function useSaveMealPattern(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (item: MealPatternItem) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternAddMod(facilityId),
        item
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to save meal pattern item', 'error');
    },
  });
}

/**
 * Snapshot-log mutation — call AFTER an Add finishes (parent +
 * children all saved) to mirror the WinForms `MealPattern_Log`
 * write:
 *
 *   Dim mpLOG As New MealPattern_Log(cnnString, rowLoad, Nothing,
 *       False, isGlobal, AIMRejectType.None, childDV, Nothing, daysChanged)
 *   mpLOG.FillDatatables()
 *   mpLOG.InsertMPLog(LogMethod.ByUser)
 *
 * Server returns 200 unconditionally; failures are swallowed so a
 * snapshot-log error never undoes the user's save.
 */
export function useLogMealPatternAdd(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: { parentPKey: number; isGlobal: boolean; daysChanged: boolean }) => {
      await apiClient.post(
        API.mpLogAdd(facilityId, patientId, input.parentPKey),
        { isGlobal: input.isGlobal, daysChanged: input.daysChanged },
      );
    },
  });
}

/**
 * Modify-snapshot log — mirrors the WinForms write that compares
 * the row + children state at dialog-open time against the
 * post-save state on disk:
 *
 *   Dim mpLOG As New MealPattern_Log(cnnString, rowOriginal,
 *       rowCurrent, False, isGlobal, AIMRejectType.None,
 *       dvChildrenOriginal, dvChildrenCurrent, daysChanged)
 *
 * Caller MUST send the original (pre-edit) parent + children
 * captured when the dialog opened. The server reads the current
 * state from DB and lets the legacy class compute the diff.
 */
/**
 * Bulk-inactive-change snapshot log — mirrors the WinForms write
 * fired once per affected meal-pattern row after Bulk Activate or
 * Bulk Deactivate. Server reads each affected row from DB (already
 * carries the new Inactive value), synthesizes the "original" by
 * flipping the bit, and writes one snapshot per row. Returns 200
 * unconditionally; failures are swallowed.
 *
 * Fire AFTER the bulk activate mutation succeeds.
 */
export function useLogMealPatternBulkInactive(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: { newInactiveValue: boolean }) => {
      await apiClient.post(
        API.mpLogBulkInactive(facilityId, patientId),
        { newInactiveValue: input.newInactiveValue },
      );
    },
  });
}

/**
 * "Make Into Global" — writes BOTH tblLog_Interaction (Add for
 * the new template, Location=GlobalMealPatternAddMod) AND
 * tblLog_Prd_Meal_Pattern via InsertMPLog_ResidentToGlobal
 * (legacy → patient-instance transition snapshot).
 *
 * Caller flow:
 *   1) save the new global template → templatePKey
 *   2) insert patient instance from template → instancePKey
 *   3) delete the source legacy row
 *   4) fire this hook with templatePKey + instancePKey +
 *      the legacy row's pre-op DTO (captured in React state
 *      before step 3 deleted it from DB)
 *
 * Server returns 200 unconditionally; both log parts are
 * best-effort and a failure on either path won't undo the
 * user's Make Into Global action.
 */
/**
 * Delete snapshot log — mirrors the WinForms call:
 *
 *   Dim mpLOG As New MealPattern_Log(cnnString, row, Nothing,
 *       True, Not row.IsNull("PKey_Prd_Meal_Pattern_Global"),
 *       AIMRejectType.None, dvChild, Nothing, True)
 *   mpLOG.FillDatatables()
 *   mpLOG.InsertMPLog(LogMethod.ByUser)
 *
 * isDelete=true is what makes the transaction type
 * `Deleted_MealPattern` on tblLog_Transaction so the row shows
 * up in the Deleted Items view + can be restored.
 *
 * Caller MUST snapshot the parent row + its children BEFORE
 * firing the delete mutation — by the time this log endpoint
 * runs, the live rows are gone.
 */
export function useLogMealPatternDelete(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      isGlobal: boolean;
      originalParent: Record<string, unknown>;
      originalChildren: Array<Record<string, unknown>>;
    }) => {
      await apiClient.post(
        API.mpLogDelete(facilityId, patientId),
        {
          isGlobal: input.isGlobal,
          originalParent: input.originalParent,
          originalChildren: input.originalChildren,
        },
      );
    },
  });
}

/**
 * Delete-All snapshot log — mirrors the WinForms loop:
 *
 *   For each row:
 *     Dim mpLOG As New MealPattern_Log(cnnString, row, Nothing,
 *         True, Not row.IsNull("PKey_Prd_Meal_Pattern_Global"),
 *         AIMRejectType.None, dvChild, Nothing, False)
 *     mpLOG.FillDatatables()
 *     mpLOG.InsertMPLog(LogMethod.ByUser)
 *
 * Caller MUST snapshot every affected row + its children from
 * React Query cache BEFORE firing the DeleteAll mutation —
 * by log time the live rows are gone. One server-side iteration
 * writes one log per entry. menuDaysChanged=false (the
 * single-row Delete variant passes true; Delete All passes false
 * per the WinForms VB).
 */
export function useLogMealPatternDeleteAll(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      entries: Array<{
        isGlobal: boolean;
        originalParent: Record<string, unknown>;
        originalChildren: Array<Record<string, unknown>>;
      }>;
    }) => {
      await apiClient.post(
        API.mpLogDeleteAll(facilityId, patientId),
        { entries: input.entries },
      );
    },
  });
}

/**
 * Reset to Global snapshot log — mirrors the WinForms call:
 *
 *   Dim mpLOG As New MealPattern_Log(cnnString, rowOriginal,
 *       rowNew, False, True, AIMRejectType.None,
 *       dvChildren, dvChildren, False)
 *   mpLOG.FillDatatables()
 *   mpLOG.InsertMPLog(LogMethod.ByUser)
 *
 * Caller MUST snapshot the patient-instance row state from React
 * Query cache BEFORE firing the reset mutation — the live row
 * is overwritten with the template's values by the time we'd
 * read it from DB. isGlobal=true is hardcoded server-side
 * (Reset only runs on global-instance rows). Children are read
 * post-reset on the server and reused for both DataViews
 * (no child diff captured — matches WinForms).
 */
export function useLogMealPatternResetToGlobal(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      globalInstancePKey: number;
      originalParent: Record<string, unknown>;
    }) => {
      await apiClient.post(
        API.mpLogResetToGlobal(facilityId, patientId),
        {
          globalInstancePKey: input.globalInstancePKey,
          originalParent: input.originalParent,
        },
      );
    },
  });
}

export function useLogMealPatternMakeIntoGlobal(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      newGlobalTemplatePKey: number;
      newPatientInstancePKey: number;
      // System.Text.Json on the server has
      // PropertyNameCaseInsensitive=true, so camelCase JSON keys
      // map cleanly onto the C# PascalCase DTO.
      originalLegacyParent: Record<string, unknown>;
    }) => {
      await apiClient.post(
        API.mpLogMakeIntoGlobal(facilityId, patientId),
        {
          newGlobalTemplatePKey: input.newGlobalTemplatePKey,
          newPatientInstancePKey: input.newPatientInstancePKey,
          originalLegacyParent: input.originalLegacyParent,
        },
      );
    },
  });
}

export function useLogMealPatternModify(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      parentPKey: number;
      isGlobal: boolean;
      daysChanged: boolean;
      // Server fields use PascalCase to match the C# DTOs.
      originalParent: Record<string, unknown>;
      originalChildren: Array<Record<string, unknown>>;
    }) => {
      await apiClient.post(
        API.mpLogModify(facilityId, patientId, input.parentPKey),
        {
          isGlobal: input.isGlobal,
          daysChanged: input.daysChanged,
          originalParent: input.originalParent,
          originalChildren: input.originalChildren,
        },
      );
    },
  });
}

export function useDeleteMealPattern(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.mealPatternDelete(facilityId, pKey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      // The Deleted Items history dialog reads from this cache;
      // without an invalidation the deleted row doesn't show up
      // until a full-page refresh.
      queryClient.invalidateQueries({ queryKey: historyKeys.mpDeleted(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to delete meal pattern item', 'error');
    },
  });
}

export function useBulkActivate(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (activate: boolean) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternBulkActivate(facilityId, patientId, activate),
      );
      return data;
    },
    onSuccess: (count, activate) => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      addNotification(`${count} items ${activate ? 'activated' : 'deactivated'}`, 'success');
    },
    onError: () => {
      addNotification('Bulk operation failed', 'error');
    },
  });
}

export function useDeleteAllMealPatterns(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.delete<number>(
        API.mealPatternDeleteAll(facilityId, patientId),
      );
      return data;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: historyKeys.mpDeleted(facilityId, patientId) });
      addNotification(`${count} items deleted`, 'success');
    },
    onError: () => {
      addNotification('Failed to delete all items', 'error');
    },
  });
}

// --- Material Colors ---

export function useMaterialColors(facilityId: number) {
  return useQuery({
    queryKey: ['materialColors', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MaterialColor[]>(
        API.materialColors(facilityId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Global Item Library ---

export function useGlobalItems(facilityId: number, groupPKey: number | null) {
  return useQuery({
    queryKey: groupPKey
      ? mealPatternKeys.globalItemsByGroup(facilityId, groupPKey)
      : mealPatternKeys.globalItems(facilityId),
    queryFn: async () => {
      const url = groupPKey
        ? API.mealPatternGlobalItemsByGroup(facilityId, groupPKey)
        : API.mealPatternGlobalItems(facilityId);
      const { data } = await apiClient.get<MealPatternGlobalItem[]>(url);
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// Template-only global items (FKey_Patient IS NULL) with display-name
// JOINs populated server-side. Used by the standalone Global Meal Pattern
// page so the grid columns (menu category, consistency, portion, etc.)
// render without needing client-side reference-data lookups.
export function useGlobalItemTemplates(facilityId: number) {
  return useQuery({
    queryKey: [...mealPatternKeys.globalItems(facilityId), 'templates'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternGlobalItem[]>(
        API.mealPatternGlobalItemsTemplates(facilityId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// Children of every global template item, joined with display names.
// Drives the master-detail child grid on the global meal pattern page.
export function useGlobalItemTemplateChildren(facilityId: number) {
  return useQuery({
    queryKey: [...mealPatternKeys.globalItems(facilityId), 'templates', 'children'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternGlobalChildItem[]>(
        API.mealPatternGlobalItemTemplateChildren(facilityId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// --- Global Meal Pattern CRUD ---

// Group PKeys currently linked to a given global item. Used by the
// AddMod dialog so the group checklist can pre-check rows accurately
// (rather than parsing the display string).
export function useGlobalItemGroupIds(facilityId: number, pKey: number) {
  return useQuery({
    queryKey: [...mealPatternKeys.globalItems(facilityId), 'groups', pKey] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<number[]>(
        API.mealPatternGlobalItemGroupIds(facilityId, pKey),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && pKey > 0,
    staleTime: 30 * 1000,
  });
}

// Helper to invalidate every Meal Pattern query touched by a Global
// mutation. Covers:
//   • Global templates list (`globalItems`) — the Global page grid.
//   • Patient-scoped keys (`byPatient`, `children`) — the server's
//     `PropagateUpdateToPatientRows` cascade (MealPatternDAL.cs:1155)
//     smart-merges template field changes into every linked patient
//     mirror row, so the patient Meal Pattern tab's cached data goes
//     stale the moment we save a template. Invalidating by
//     `['mealPattern', 'byPatient', facilityId]` prefix catches every
//     patient at once (React Query is prefix-based) — we don't need to
//     enumerate which patients were actually touched.
// Inactive query caches are just marked stale and refetch on next
// mount, so hitting the broad namespace has negligible perf cost.
function invalidateGlobalQueries(qc: ReturnType<typeof useQueryClient>, facilityId: number) {
  qc.invalidateQueries({ queryKey: mealPatternKeys.globalItems(facilityId) });
  qc.invalidateQueries({ queryKey: [...mealPatternKeys.all, 'byPatient', facilityId] });
  qc.invalidateQueries({ queryKey: [...mealPatternKeys.all, 'children', facilityId] });
}

/** Response shape from `POST /api/MealPattern/GlobalItems/AddMod/{fac}`.
 *  Server-side type: `MealPatternDAL.GlobalMealPatternAddModResult`.
 *  `patientRowsAffected` is 0 on inserts and on edits where no linked
 *  patient mirror row still matched the OLD template values (the
 *  smart-merge in `PropagateUpdateToPatientRows` skipped them). */
export interface GlobalMealPatternAddModResult {
  pKey: number;
  patientRowsAffected: number;
  /** Residents given a NEW template because they already have one of its
   *  groups (sent `addToGroupResidents`). Absent on older API builds. */
  patientRowsAdded?: number;
}

export function useSaveGlobalMealPattern(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (payload: {
      item: MealPatternGlobalItem;
      groupIds: number[] | null;
      /** Global Meal Patterns page only: a NEW template also goes to every
       *  resident who already has one of its groups (WinForms
       *  InsertPatientGroupRows). Off for resident-tab flows. */
      addToGroupResidents?: boolean;
    }) => {
      // The C# DTO uses PascalCase `Item` + `GroupIds`. With FirstChar-
      // LowerPolicy on inbound JSON the server reads `item` / `groupIds`,
      // but Newtonsoft.Json on TblPrd_Meal_Pattern_Global mostly leaves
      // the existing camelCase mapping — send the React shape directly
      // and let the server bind.
      //
      // `groupIds === null` is meaningful — it tells the server to
      // leave existing group_link rows alone. Used by Rename Mode's
      // inline edit on the main grid which only changes CommonName.
      const { data } = await apiClient.post<GlobalMealPatternAddModResult>(
        API.mealPatternGlobalAddMod(facilityId),
        {
          item: payload.item,
          groupIds: payload.groupIds,
          addToGroupResidents: payload.addToGroupResidents ?? false,
        },
      );
      return data;
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to save global meal pattern item', 'error'),
  });
}

/** Response shape from `POST /api/MealPattern/GlobalItems/BulkSetInactive/{fac}`.
 *  `itemsUpdated` = number of template rows whose Inactive was flipped
 *  (excludes templates already at the target value). `patientRowsAffected`
 *  = smart-merged patient mirror rows (patient's Inactive still matched
 *  the template's OLD Inactive). */
export interface GlobalMealPatternBulkSetInactiveResult {
  itemsUpdated: number;
  patientRowsAffected: number;
}

/** Toolbar "Entire Group" bulk Activate/Deactivate for the MP tab.
 *  One transaction on the server flips every template in the group
 *  AND smart-merges patient mirror rows. Client shows both counts
 *  on the toast and lets the shared global-invalidation refresh the
 *  grid + every patient's cached MP list. */
export function useBulkSetInactiveGlobalMealPattern(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    // groupPKey === null → server treats it as the (None) scope
    // (every template with no group-link rows). Non-null → the
    // specific group's templates.
    // pKeys: the templates in the page's current view; only those flip.
    mutationFn: async (payload: { groupPKey: number | null; newInactive: boolean; pKeys?: number[] }) => {
      const { data } = await apiClient.post<GlobalMealPatternBulkSetInactiveResult>(
        API.mealPatternGlobalBulkSetInactive(facilityId),
        { groupPKey: payload.groupPKey, newInactive: payload.newInactive, pKeys: payload.pKeys ?? null },
      );
      return data;
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to update group', 'error'),
  });
}

export function useDeleteGlobalMealPattern(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.mealPatternGlobalDelete(facilityId, pKey));
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: (error) => addNotification(globalDeleteErrorMessage(error), 'error'),
  });
}

/** One key's outcome in a multi-select Delete (server: GlobalBulkDeleteItem). */
export interface GlobalBulkDeleteItem {
  pKey: number;
  status: 'deleted' | 'refused' | 'notFound' | 'failed';
  reason?: string | null;
}

/** `POST .../DeleteBatch/{fac}` response (server: GlobalBulkDeleteResult). */
export interface GlobalBulkDeleteResult {
  deleted: number;
  refused: number;
  notFound: number;
  failed: number;
  items: GlobalBulkDeleteItem[];
}

/** A per-key count (server: GlobalKeyCount); keys with none are left out. */
export interface GlobalKeyCount {
  pKey: number;
  count: number;
}

/**
 * Deletes several global meal pattern templates: each key gets the single delete's checks
 * and is deleted on its own; refused keys come back with the reason. The server writes
 * each deleted key's Delete log row (so Deleted Items lists every one).
 */
export function useDeleteGlobalMealPatternBatch(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (pKeys: number[]) => {
      const { data } = await apiClient.post<GlobalBulkDeleteResult>(API.mealPatternGlobalDeleteBatch(facilityId), { pKeys });
      return data;
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: (error) => addNotification(globalDeleteErrorMessage(error), 'error'),
  });
}

/**
 * A UsageCounts row. `count` is every resident holding a copy, discharged and deleted
 * included (the multi-row Delete confirm). `activeCount` and `facilityCount` are the
 * active residents and their facilities (the impact line). An API from before they
 * were added sends `count` only.
 */
export interface GlobalItemUsageCount extends GlobalKeyCount {
  activeCount?: number;
  facilityCount?: number;
}

/** Residents using each template (the count GlobalItems/Usage lists), for the multi-row confirm. */
export async function fetchGlobalItemUsageCounts(facilityId: number, pKeys: number[]): Promise<GlobalItemUsageCount[]> {
  const { data } = await apiClient.post<GlobalItemUsageCount[]>(API.mealPatternGlobalItemUsageCounts(facilityId), { pKeys });
  return data ?? [];
}

/** The residents holding one template, every facility, discharged and deleted included. */
export async function fetchGlobalItemUsage(facilityId: number, pKey: number): Promise<GlobalMealPatternUsagePatient[]> {
  const { data } = await apiClient.get<GlobalMealPatternUsagePatient[]>(API.mealPatternGlobalItemUsage(facilityId, pKey));
  return data ?? [];
}

/** A Groups/UsageCounts row: the group's templates, and the active residents holding any of them. */
export interface GlobalGroupUsageCount {
  groupPKey: number;
  items: number;
  residents: number;
}

/** Every global meal pattern group with its counts, empty groups included. */
export async function fetchGlobalGroupUsageCounts(facilityId: number): Promise<GlobalGroupUsageCount[]> {
  const { data } = await apiClient.get<GlobalGroupUsageCount[]>(API.mealPatternGlobalGroupUsageCounts(facilityId));
  return data ?? [];
}

/**
 * The reason the API refused a global template delete (`{ error }` on a 403, 404 or 409:
 * the WinForms guards in MealPatternController.DeleteGlobalRefusal, e.g. "This item/group
 * is currently in use by another facility and cannot be deleted at this time."), or the
 * generic message.
 */
export function globalDeleteErrorMessage(
  error: unknown,
  fallback = 'Failed to delete global meal pattern item',
): string {
  const body = (error as { response?: { data?: unknown } } | null)?.response?.data;
  const reason = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;
  return typeof reason === 'string' && reason.trim() ? reason : fallback;
}

export function useSaveGlobalMealPatternChild(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (child: MealPatternGlobalChildItem) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternGlobalChildAddMod(facilityId),
        child,
      );
      return data;
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to save global child item', 'error'),
  });
}

export function useDeleteGlobalMealPatternChild(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.mealPatternGlobalChildDelete(facilityId, pKey));
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to delete global child item', 'error'),
  });
}

export function useReorderGlobalChildPriority(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (items: PriorityReorder[]) => {
      await apiClient.post(API.mealPatternGlobalChildReorderPriority(facilityId), items);
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to reorder global child priorities', 'error'),
  });
}

// --- Global Meal Pattern Groups CRUD ---

export function useGlobalMealPatternGroups(facilityId: number) {
  return useQuery({
    queryKey: mealPatternKeys.globalGroups(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternGlobalGroup[]>(
        API.mealPatternGetGlobalGroups(facilityId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

// Invalidate both the groups list AND the global items list — the
// `groupNames` column on items rolls up the linked group names, so
// renaming or deleting a group can change item rows too.
function invalidateGroupQueries(qc: ReturnType<typeof useQueryClient>, facilityId: number) {
  qc.invalidateQueries({ queryKey: mealPatternKeys.globalGroups(facilityId) });
  qc.invalidateQueries({ queryKey: mealPatternKeys.globalItems(facilityId) });
}

export function useSaveGlobalMealPatternGroup(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (group: MealPatternGlobalGroup) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternGlobalGroupAddMod(facilityId),
        group,
      );
      return data;
    },
    onSuccess: () => invalidateGroupQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to save group', 'error'),
  });
}

// Item-level usage: patients across all facilities whose meal pattern
// row points back to the given global template PKey. Drives the
// Tools → Usage dialog. Enabled only when a real PKey is supplied.
export function useGlobalItemUsage(facilityId: number, pKey: number) {
  return useQuery({
    queryKey: mealPatternKeys.globalItemUsage(facilityId, pKey),
    queryFn: () => fetchGlobalItemUsage(facilityId, pKey),
    enabled: facilityId > 0 && pKey > 0,
    staleTime: 30 * 1000,
  });
}

// Candidate list for the Replace dialog — other global templates that
// share the focused row's FKey_Item.
export function useReplaceCandidates(facilityId: number, focusedPKey: number) {
  return useQuery({
    queryKey: [
      ...mealPatternKeys.globalItems(facilityId),
      'replaceCandidates',
      focusedPKey,
    ] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<MealPatternGlobalItem[]>(
        API.mealPatternGlobalReplaceCandidates(facilityId, focusedPKey),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && focusedPKey > 0,
    staleTime: 30 * 1000,
  });
}

// Run the replace: every patient row referencing focusedPKey gets
// migrated onto replacementPKey on the server. Invalidates the global
// items list so the grid + master-detail children refetch.
export function useReplaceGlobalItem(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (params: { focusedPKey: number; replacementPKey: number }) => {
      await apiClient.post(
        API.mealPatternGlobalReplace(facilityId, params.focusedPKey, params.replacementPKey),
      );
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to replace item', 'error'),
  });
}

// Hide a global meal pattern item for the current facility. Inserts /
// updates a row in tblPrd_Meal_Pattern_Global_Facility with Hidden=true.
// Invalidates the global items list so the grid (and any hidden-row
// filter) refetches.
export function useHideGlobalItem(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.post(API.mealPatternGlobalHide(facilityId, pKey));
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to hide item', 'error'),
  });
}

// Unhide a global meal pattern item for the current facility. Inverse
// of useHideGlobalItem — clears the Hidden flag, deleting the
// tblPrd_Meal_Pattern_Global_Facility row entirely when nothing else
// (Private) keeps it alive. No-op server-side if the item wasn't
// already hidden for this facility.
export function useUnhideGlobalItem(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.post(API.mealPatternGlobalUnhide(facilityId, pKey));
    },
    onSuccess: () => invalidateGlobalQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to unhide item', 'error'),
  });
}

export function useDeleteGlobalMealPatternGroup(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API.mealPatternGlobalGroupDelete(facilityId, id));
    },
    onSuccess: () => invalidateGroupQueries(queryClient, facilityId),
    onError: () => addNotification('Failed to delete group', 'error'),
  });
}

// Create a patient-linked row in `tblPrd_Meal_Pattern_Global` that
// points at the given template via FKey_Prd_Meal_Pattern_Global. The
// patient grid then renders it as an `IsGlobal=true` row, still tied
// to the template for future propagations. Used by the patient grid's
// Global → New From Existing flow after the template has been saved.
export function useInsertPatientGlobalFromTemplate(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (templatePKey: number) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternInsertPatientGlobalFromTemplate(facilityId, patientId, templatePKey),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to add global item to patient', 'error');
    },
  });
}

// --- Add Global Items Dialog ---
// Loads ALL facility-visible global templates combined by item identity
// (the WinForms CombineMPRows behavior). Mealtime-specific template
// PKeys are pivoted into pKey_B..pKey_HS2 fields. Cached aggressively
// since templates change rarely relative to the dialog lifecycle.

export function useGlobalTemplatesCombined(facilityId: number, enabled: boolean = true) {
  const { addNotification } = useNotificationStore();
  return useQuery({
    queryKey: ['mealPatternGlobalTemplatesCombined', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<GlobalTemplateCombined[]>(
        API.mealPatternGlobalTemplatesCombined(facilityId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0,
    staleTime: 60_000,
    meta: {
      onError: () => addNotification('Failed to load global templates', 'error'),
    },
  });
}

// Batch-insert patient-linked global instances for the given template
// PKeys. Server handles each as a copy from template → patient (same
// path as useInsertPatientGlobalFromTemplate, just wrapped in one
// transaction).
export function useInsertPatientGlobalBatch(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (templatePKeys: number[]) => {
      // Server now returns the list of new patient-instance PKeys
      // (one per template successfully instantiated). Callers use
      // `.length` for the success-notification count and the array
      // itself for the per-instance log fan-out via
      // `useLogMealPatternAddGlobalItems`.
      const { data } = await apiClient.post<number[]>(
        API.mealPatternInsertPatientGlobalBatch(facilityId, patientId),
        templatePKeys,
      );
      return data ?? [];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to add global items to patient', 'error');
    },
  });
}

/**
 * Add-Global-Items snapshot log — mirrors the WinForms loop fired
 * after the batch insert returns:
 *
 *   For each new instance:
 *     Dim mpLogGlobal As New MealPattern_Log(cnnString, rowNew,
 *         Nothing, False, True,
 *         IIf(aimRejected, AIMRejectType.Added, AIMRejectType.None),
 *         dvChild, Nothing, False)
 *     mpLogGlobal.FillDatatables()
 *     mpLogGlobal.InsertMPLog(LogMethod.ByUser)
 *
 * Server reads each new patient-instance row + its children from
 * DB by PKey, so the React side just needs to forward the PKey
 * list returned by the batch insert.
 */
/**
 * MealPattern_Log.LogMethod enum mirrored for client use. See
 * the legacy VB class for the canonical values:
 *   - ByAIM   = 0 (Auto-Intelligent Menu drop — not used yet)
 *   - ByUser  = 1 (default — user explicitly picked templates)
 *   - ByGlobal = 2 (NFE — row dropped as part of template-create)
 *   - Custom  = 3 (not used yet)
 */
export const MP_LOG_METHOD = {
  ByAIM: 0,
  ByUser: 1,
  ByGlobal: 2,
  Custom: 3,
} as const;

export function useLogMealPatternAddGlobalItems(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: { newInstancePKeys: number[]; logMethod?: number }) => {
      if (!input.newInstancePKeys || input.newInstancePKeys.length === 0) return;
      await apiClient.post(
        API.mpLogAddGlobalItems(facilityId, patientId),
        {
          newInstancePKeys: input.newInstancePKeys,
          // Default to ByUser (Add Global Items semantics).
          // NFE callers should pass MP_LOG_METHOD.ByGlobal.
          logMethod: input.logMethod ?? MP_LOG_METHOD.ByUser,
        },
      );
    },
  });
}

export function useInsertFromGlobal(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (params: { globalPKey: number; mealtimes: number[] }) => {
      const { data } = await apiClient.post<number[]>(
        API.mealPatternInsertFromGlobal(facilityId),
        {
          fKey_Patient: patientId,
          globalPKey: params.globalPKey,
          mealtimes: params.mealtimes,
        },
      );
      return data;
    },
    onSuccess: (keys) => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      addNotification(`${keys.length} item(s) inserted from global library`, 'success');
    },
    onError: () => {
      addNotification('Failed to insert from global library', 'error');
    },
  });
}

// --- Child Item CRUD ---

export function useSaveMealPatternChild(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (child: MealPatternChildItem) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternAddModChild(facilityId),
        child,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to save child item', 'error');
    },
  });
}

export function useDeleteMealPatternChild(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.mealPatternDeleteChild(facilityId, pKey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: historyKeys.mpDeleted(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to delete child item', 'error');
    },
  });
}

// --- Priority Reorder ---

export function useReorderPriority(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (items: PriorityReorder[]) => {
      await apiClient.post(
        API.mealPatternReorderPriority(facilityId),
        items,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to reorder priorities', 'error');
    },
  });
}

// --- Copy From Resident ---
// Copies every meal-pattern item (local + global-linked) from the
// source patient to the destination (the patient identified by the
// hook's `patientId` argument). Children copy alongside. Source data
// is untouched. Returns the count of parent rows copied.

export function useCopyFromResident(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (sourcePatientId: number) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternCopyFromResident(facilityId, patientId, sourcePatientId),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to copy meal pattern from resident', 'error');
    },
  });
}

// --- Global Link Operations ---

export function useBreakGlobalLink(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKey: number) => {
      // Returns the new local PKey_Prd_Meal_Pattern of the row that
      // replaced the global instance, so callers can focus it.
      const { data } = await apiClient.post<number>(
        API.mealPatternBreakGlobalLink(facilityId, pKey),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      addNotification('Global link broken - item is now resident-specific', 'success');
    },
    onError: () => {
      addNotification('Failed to break global link', 'error');
    },
  });
}

export function useResetToGlobal(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.post(API.mealPatternResetToGlobal(facilityId, pKey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
      addNotification('Reset to global meal pattern', 'success');
    },
    onError: () => {
      addNotification('Failed to reset to global', 'error');
    },
  });
}

export function useMakeIntoGlobal(facilityId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (item: MealPatternItem) => {
      const { data } = await apiClient.post<number>(
        API.mealPatternMakeIntoGlobal(facilityId),
        item,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.globalItems(facilityId) });
      addNotification('Item promoted to global meal pattern', 'success');
    },
    onError: () => {
      addNotification('Failed to promote to global', 'error');
    },
  });
}
